/* eslint-disable prettier/prettier */
import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { message, Modal } from 'antd';
import moment from 'moment';
import { getSignedUrl, newPodUpload } from 'store/commonSlice';
import { addUser, getListAction, updateProofOfDelivery } from 'store/pageSlice';
import { getClientList } from 'store/clientSlice';
import { getAllTrackingInfoV2 } from 'store/tripSliceV2';
import { getUIVersion } from 'utils/router-static-data';
import { TRIP_STATUS_ID } from 'utils/tripStatus';

const DATE_FORMAT = 'MM/DD/YYYY';
const DATETIME_FORMAT = 'MM/DD/YYYY HH:mm';
const V2_DATE_FORMAT = 'YYYY-MM-DD';
const V2_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

// Constants for error messages
const ERROR_MESSAGES = {
	CLIENT_REFERENCE_NOT_FOUND: 'Client reference numbers not found in the file',
	FAILED_TO_UPDATE_POD: 'Failed to update upload_proof_of_delivery',
	FORCE_UPLOAD_FAILED:
		'Upload failed: Client reference numbers not found in the file, even after force upload.',
};

const hasValue = (value) => value !== undefined && value !== null && value !== '';

// Charge form field -> proof_of_delivery.additional_charges key. Single source of
// truth for both directions: the submit payload is built from it, and the values
// saved by a previous (restricted) close attempt are read back through it.
const CHARGE_FIELD_MAP = {
	haltVendor: 'haltCharges',
	haltClient: 'clientHaltCharges',
	miscVendor: 'miscellaneousCharges',
	miscClient: 'clientMiscellaneousCharges',
	tollVendor: 'tollCharges',
	tollClient: 'clientTollCharges',
	unloadVendor: 'unloadingCharges',
	unloadClient: 'clientUnloadingCharges',
	extraKmVendor: 'extraKmCharges',
	extraKmClient: 'clientExtraKmCharges',
	extraHrVendor: 'extraHrCharges',
	extraHrClient: 'clientExtraHrCharges',
	labourVendor: 'labourCharges',
	labourClient: 'clientLabourCharges',
	deductionVendor: 'deductionAmount',
	deductionClient: 'clientDeductionAmount',
};

const CHARGE_FIELDS = Object.keys(CHARGE_FIELD_MAP);

const initialCharges = () => CHARGE_FIELDS.reduce((acc, field) => ({ ...acc, [field]: null }), {});

/**
 * Normalize a trip_documents / POD document value to an array of S3 key strings.
 * Mirrors DocumentsTab's normalizeDocKeys — these fields are variously stored as a
 * bare string, an object with document_key, or an array of either.
 */
const normalizeDocKeys = (value) => {
	if (!value) return [];
	if (typeof value === 'string') return [value];
	if (!Array.isArray(value) && typeof value === 'object' && value.document_key) {
		return [value.document_key];
	}
	if (Array.isArray(value)) {
		return value
			.map((item) => {
				if (typeof item === 'string') return item;
				if (item && typeof item === 'object' && item.document_key) return item.document_key;
				return null;
			})
			.filter(Boolean);
	}
	return [];
};

/**
 * additional_charges (as stored on the POD, and as surfaced by the Expenses tab's
 * margin statement) -> charge form state. Amounts are held as digit strings to match
 * what handleChange writes; anything unset stays null so the field renders empty.
 */
const chargesFromAdditionalCharges = (additionalCharges) => {
	const source =
		additionalCharges && typeof additionalCharges === 'object' ? additionalCharges : {};
	return CHARGE_FIELDS.reduce((acc, field) => {
		const raw = source[CHARGE_FIELD_MAP[field]];
		if (!hasValue(raw)) {
			return { ...acc, [field]: null };
		}
		const amount = Number(raw);
		return { ...acc, [field]: Number.isFinite(amount) ? String(Math.round(amount)) : null };
	}, {});
};

const hasAnyCharge = (chargeState) =>
	CHARGE_FIELDS.some((field) => hasValue(chargeState?.[field]) && Number(chargeState[field]) > 0);

const parseActualEndTime = (value) => {
	if (!hasValue(value)) return null;
	const parsed = moment(
		value,
		[moment.ISO_8601, 'MM/DD/YYYY HH:mm:ss', 'MM/DD/YYYY HH:mm'],
		true
	);
	if (parsed.isValid()) {
		return parsed;
	}
	const parsedValue = moment(value);
	return parsedValue.isValid() ? parsedValue : null;
};
// eslint-disable-next-line sonarjs/cognitive-complexity
export const useEndTripModal = (trip, visible, onClose, tableRefreshRef, setpaginateConfig) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { tripId: urlTripId } = useParams();
	const resolvedTripId = trip?.id || trip?.trip_id || urlTripId;
	const [trackingInfoData, setTrackingInfoData] = useState(null);
	const isIntercity = Boolean(trip?.info?.is_inter_city);
	const isV2UI = getUIVersion() === 'new';
	const uploadTypeLabel = isIntercity ? 'POD' : 'odometer reading';
	const resolvedPodRequired =
		trip?.trip_info?.pod_required ?? trackingInfoData?.trip_info?.pod_required;
	const isPodRequired = isV2UI ? Boolean(resolvedPodRequired) : isIntercity;
	const shouldCollectEndKm = isV2UI || !isIntercity;
	const shouldRequireOdometer = isV2UI ? false : !isIntercity; // Odometer upload is optional in V2
	const shouldRequirePod = isV2UI ? isPodRequired : isIntercity;
	const backendEndTime = trip?.actual_end_time || null;
	const backendEndKm = trip?.end_km ?? null;
	// const resolvedStartKm = parseNumericValue(
	// 	trackingInfoData?.actual_start_km ?? trip?.actual_start_km ?? trip?.start_km ?? null
	// );
	// const resolvedAccurateDistanceKm = parseNumericValue(trackingInfoData?.accurate_distance_km);
	// const resolvedOdometerDistanceKm = parseNumericValue(trackingInfoData?.odometer_distance_km);
	const computedEndKmPrefill = backendEndKm;
	const isBackendEndTimeLocked = hasValue(backendEndTime);
	const isBackendEndKmLocked = hasValue(backendEndKm);
	const isComputedEndKmLocked =
		!isBackendEndKmLocked && shouldCollectEndKm && computedEndKmPrefill !== null;
	const shouldFetchTrackingInfo =
		resolvedPodRequired === undefined ||
		!hasValue(trip?.actual_end_time) ||
		!hasValue(trip?.actual_end_km ?? trip?.end_km);

	useEffect(() => {
		setTrackingInfoData(null);
	}, [resolvedTripId, visible]);

	useEffect(() => {
		if (!visible || !isV2UI || !resolvedTripId || !shouldFetchTrackingInfo) {
			return undefined;
		}

		let cancelled = false;

		dispatch(getAllTrackingInfoV2({ trip_id: resolvedTripId }))
			.then((res) => {
				const trackingData = res?.data || null;
				if (cancelled || !trackingData) return;

				setTrackingInfoData(trackingData);
			})
			.catch((error) => {
				if (cancelled) return;
				console.error('Failed to fetch tracking trip info', error);
			});

		return () => {
			cancelled = true;
		};
	}, [visible, isV2UI, resolvedTripId, shouldFetchTrackingInfo, dispatch]);

	// State management
	const [date, setDate] = useState(null);
	const [endKm, setEndKm] = useState('');
	const [s3Key, setS3Key] = useState(null);
	const [podS3KeysState, setPodS3KeysState] = useState([]);
	const [odometerS3KeyState, setOdometerS3KeyState] = useState('');
	const [uploading, setUploading] = useState(false);
	const [uploadError, setUploadError] = useState('');
	const [charges, setCharges] = useState(initialCharges());
	const [loading, setLoading] = useState(false);
	const [showPodQuestionModal, setShowPodQuestionModal] = useState(false);
	const [pendingUploadFile, setPendingUploadFile] = useState(null);
	const [showForceUploadModal, setShowForceUploadModal] = useState(false);
	const [pendingForceUploadFile, setPendingForceUploadFile] = useState(null);
	const [trip_reason, setTripReason] = useState([]);
	const [customerProofS3Key, setCustomerProofS3Key] = useState(null);
	const [customerProofS3KeysState, setCustomerProofS3KeysState] = useState([]);
	const [uploadingProof, setUploadingProof] = useState(false);
	const [uploadProofError, setUploadProofError] = useState('');
	const [driverPodPreviewKeys, setDriverPodPreviewKeys] = useState([]);

	useEffect(() => {
		if (!visible || !isV2UI) return undefined;

		const normalizeKeys = (value) => {
			if (Array.isArray(value)) return value.filter(Boolean);
			if (typeof value === 'string') {
				return value
					.split(',')
					.map((item) => item.trim())
					.filter(Boolean);
			}
			return [];
		};

		const nextDriverPodPreviewKeys = normalizeKeys(
			trackingInfoData?.driver_final_pod?.pod_urls
		);
		setDriverPodPreviewKeys(nextDriverPodPreviewKeys);
		return undefined;
	}, [visible, isV2UI, trackingInfoData, trip]);

	const podS3Keys = (() => {
		if (isV2UI) return podS3KeysState;
		if (s3Key && isIntercity) return [s3Key];
		return [];
	})();
	const setPodS3Keys = useCallback(
		(files) => {
			if (isV2UI) {
				setPodS3KeysState(files || []);
				return;
			}
			setS3Key(files?.[0] || null);
		},
		[isV2UI]
	);
	const odometerS3Key = (() => {
		if (isV2UI) return odometerS3KeyState;
		if (isIntercity) return '';
		return s3Key || '';
	})();
	const setOdometerS3Key = useCallback(
		(value) => {
			if (isV2UI) {
				setOdometerS3KeyState(value || '');
				return;
			}
			setS3Key(value || null);
		},
		[isV2UI]
	);
	const customerProofS3Keys = (() => {
		if (isV2UI) return customerProofS3KeysState;
		if (customerProofS3Key) return [customerProofS3Key];
		return [];
	})();
	const setCustomerProofS3Keys = useCallback(
		(files) => {
			if (isV2UI) {
				setCustomerProofS3KeysState(files || []);
				return;
			}
			setCustomerProofS3Key(files?.[0] || null);
		},
		[isV2UI]
	);

	// Reset state when modal opens/closes
	useEffect(() => {
		dispatch(
			getClientList({
				search_type: 'trip_end_reason',
				filter_by: {},
			})
		).then((res) => {
			const reason_data = res?.data?.map((item) => ({
				value: item?.id,
				label: item?.reason,
			}));
			setTripReason(reason_data || []);
		});
		if (visible) {
			setDate(null);
			setEndKm('');
			setS3Key(null);
			setPodS3KeysState([]);
			setOdometerS3KeyState('');
			setDriverPodPreviewKeys([]);
			setUploading(false);
			setUploadError('');
			setCharges(initialCharges());
			setShowPodQuestionModal(false);
			setPendingUploadFile(null);
			setShowForceUploadModal(false);
			setPendingForceUploadFile(null);
			setShowTripEndReasonModal(false);
			setTripEndReason(null);
			setCustomerProofS3Key(null);
			setCustomerProofS3KeysState([]);
			setUploadingProof(false);
			setUploadProofError('');
		}
	}, [visible, dispatch]);

	useEffect(() => {
		if (!visible) {
			return;
		}

		const prefilledDate = parseActualEndTime(backendEndTime);
		if (prefilledDate) {
			setDate(prefilledDate);
		}

		if (hasValue(backendEndKm)) {
			setEndKm(String(backendEndKm));
			return;
		}

		if (shouldCollectEndKm && computedEndKmPrefill !== null) {
			setEndKm(String(computedEndKmPrefill));
			return;
		}

		setEndKm('');
	}, [visible, backendEndTime, backendEndKm, shouldCollectEndKm, computedEndKmPrefill]);

	// Prefill from a previous close attempt.
	//
	// These effects are declared after the reset effect above on purpose. Both run in the
	// same commit when visible flips, and React runs effects in declaration order, so
	// prefill has to come last or the reset would blank it out again.
	//
	// Documents come straight off the trip row: trip_documents is the same field the
	// Documents tab lists, so nothing extra has to be fetched. V2 only, so the legacy UI
	// keeps its existing behaviour untouched.
	const tripDocumentsFromRow = trip?.trip_documents;

	useEffect(() => {
		if (!visible || !isV2UI) return;

		const odometerEndKeys = normalizeDocKeys(tripDocumentsFromRow?.odometer_end);
		if (odometerEndKeys.length) {
			setOdometerS3KeyState(odometerEndKeys[0]);
		}

		const chargeProofKeys = normalizeDocKeys(
			tripDocumentsFromRow?.additional_charges_proof_key
		);
		if (chargeProofKeys.length) {
			setCustomerProofS3KeysState(chargeProofKeys);
		}
	}, [visible, isV2UI, tripDocumentsFromRow]);

	// Charges and POD documents already recorded against this trip, also straight off the
	// row. additional_charges is the POD data the Expenses tab renders as the margin
	// statement; proof_of_delivery_keys are the POD documents. Deliberately not read via
	// POD search: that endpoint excludes PODs whose status is still NULL, which is every
	// placeholder POD holding only charges, i.e. any trip not requiring a POD document.
	const recordedAdditionalCharges = trip?.additional_charges;
	const recordedPodKeys = trip?.proof_of_delivery_keys;
	const hasPrefilledCharges =
		isV2UI && hasAnyCharge(chargesFromAdditionalCharges(recordedAdditionalCharges));

	useEffect(() => {
		if (!visible || !isV2UI) return;

		if (recordedAdditionalCharges) {
			setCharges(chargesFromAdditionalCharges(recordedAdditionalCharges));
		}

		const podKeys = normalizeDocKeys(recordedPodKeys);
		if (podKeys.length) {
			setPodS3KeysState(podKeys);
		}
	}, [visible, isV2UI, recordedAdditionalCharges, recordedPodKeys]);

	// True when this trip already carries a submitted-but-not-closed attempt, so the
	// modals can tell the user that the values in front of them were entered earlier.
	const isPrefilledFromPreviousAttempt =
		isBackendEndTimeLocked || isBackendEndKmLocked || hasPrefilledCharges;

	// Page navigation refresh function
	const refreshTableByPageNavigation = () => {
		if (setpaginateConfig) {
			setpaginateConfig((prev) => {
				// Reset to first page with offset 0, preserve limit dynamically
				const limit = prev?.limit ?? 10;

				return {
					...prev,
					currentPage: 1,
					offset: 0,
					limit,
				};
			});
		}
	};

	// Helper function to upload file to S3
	const uploadToS3 = async (url, file, contentType) => {
		try {
			const response = await fetch(url, {
				method: 'PUT',
				headers: { 'Content-Type': contentType },
				body: file,
			});

			if (response.ok) {
				console.log('S3 upload successful');
				return { success: true };
			}
			return { success: false, error: `HTTP ${response.status}: ${response.statusText}` };
		} catch (error) {
			return { success: false, error: error.message };
		}
	};

	// Always returns { exists: boolean, lrId: string|null }
	const fetchLrId = async () => {
		if (!isIntercity) return { exists: true, lrId: null };
		try {
			const res = await dispatch(
				getListAction({
					search_type: 'consignment_note',
					trip_id: resolvedTripId,
					...(isV2UI && { version: 'v2' }),
				})
			);
			const lrDetails = res?.rows?.[0] || null;
			return { exists: !!lrDetails, lrId: lrDetails?.id || null };
		} catch {
			return { exists: false, lrId: null };
		}
	};

	// Handle client reference number errors (only for intercity trips)
	const handleClientReferenceError = (errorMessage, force, uploadFile, lrIdToUse) => {
		// Only handle client reference errors for intercity trips
		if (!isIntercity) return false;

		const isClientReferenceError = errorMessage.includes(
			ERROR_MESSAGES.CLIENT_REFERENCE_NOT_FOUND
		);
		const isPodUpdateError = errorMessage.includes(ERROR_MESSAGES.FAILED_TO_UPDATE_POD);

		if ((isClientReferenceError || isPodUpdateError) && !force) {
			console.log('Client reference numbers not found, showing force upload modal');
			setPendingForceUploadFile({ uploadFile, lrIdToUse });
			setShowForceUploadModal(true);
			setUploading(false);
			return true;
		}

		if ((isClientReferenceError || isPodUpdateError) && force) {
			if (isV2UI) {
				setPodS3KeysState([]);
			} else {
				setS3Key(null);
			}
			setUploadError(ERROR_MESSAGES.FORCE_UPLOAD_FAILED);
			message.error(ERROR_MESSAGES.FORCE_UPLOAD_FAILED);
			setUploading(false);
			return true;
		}

		return false;
	};

	// Handle backend validation errors
	const handleBackendValidationError = (podRes, force, uploadFile, lrIdToUse) => {
		if (podRes?.error?.message) {
			const errorMessage = podRes.error.message;
			console.log('Backend validation error:', errorMessage);

			if (handleClientReferenceError(errorMessage, force, uploadFile, lrIdToUse)) {
				return true;
			}

			throw new Error(errorMessage);
		}
		return false;
	};

	// Handle Axios errors
	const handleAxiosError = (error, force, uploadFile, lrIdToUse) => {
		if (error.response?.data?.error?.message) {
			const errorMessage = error.response.data.error.message;
			const errorCode = error.response.data.error.code;

			// Prefer the structured backend error code; fall back to message regex
			// only for older backends that don't send a code yet.
			const isMissingDocsError =
				errorCode === 'VEHICLE_DOCUMENTS_PENDING' ||
				(!errorCode &&
					(/\b(rc|insurance|puc|vehicle)\s+documents?\b/i.test(errorMessage) ||
						/missing\s+(mandatory\s+)?(vehicle\s+)?documents?/i.test(errorMessage) ||
						/onboard(ed)?\s+without\s+documents?/i.test(errorMessage)));

			if (isMissingDocsError) {
				Modal.confirm({
					title: 'Missing Vehicle Documents',
					content: `${errorMessage}. Would you like to upload the missing documents now?`,
					okText: 'Upload Documents',
					cancelText: 'Cancel',
					onOk: () => {
						resetAll();
						navigate(`/vehicles/${trip.vehicle_number}`, {
							state: { uploadDocs: true },
						});
					},
				});
				return true;
			}

			if (handleClientReferenceError(errorMessage, force, uploadFile, lrIdToUse)) {
				return true;
			}

			if (uploadFile) {
				if (isV2UI) {
					setPodS3KeysState([]);
				} else {
					setS3Key(null);
				}
			}
			setUploadError(errorMessage);
			return true;
		}
		return false;
	};

	const handleUpload = async (uploadFile) => {
		if (isV2UI) {
			return false;
		}

		try {
			let lrIdToUse = null;
			if (isIntercity) {
				const { exists, lrId } = await fetchLrId();
				if (!exists) {
					setPendingUploadFile(uploadFile);
					setShowPodQuestionModal(true);
					return false;
				}
				lrIdToUse = lrId;
			}
			await doUpload(uploadFile, false, lrIdToUse);
			return false;
		} catch (error) {
			console.error('Upload error:', error);
			const errorMessage = `${uploadTypeLabel} upload failed due to network error`;
			setUploadError(errorMessage);
			message.error(errorMessage);
			return false;
		}
	};

	// Charge form state -> additional_charges payload, via CHARGE_FIELD_MAP so this
	// stays the exact inverse of the prefill above.
	const createChargesObject = () =>
		CHARGE_FIELDS.reduce(
			(acc, field) => ({
				...acc,
				[CHARGE_FIELD_MAP[field]]: charges[field] ? Number(charges[field]) : null,
			}),
			{}
		);

	// Validate file size
	const validateFileSize = (uploadFile) => {
		if (uploadFile.size > 5 * 1024 * 1024) {
			message.error('File size must be less than 5 MB');
			return false;
		}
		return true;
	};

	// Get signed URL for upload
	const getSignedUrlForUpload = async (uploadFile) => {
		const entityType = isIntercity ? 'proof_of_delivery_upload' : 'trip';
		const res = await dispatch(
			getSignedUrl({
				url_type: 'upload',
				data: { entity: entityType, keys: [uploadFile.name] },
			})
		);

		const fileData = res?.data?.[0];
		if (!fileData?.url || !fileData?.filename) {
			throw new Error('Failed to get signed URL from server');
		}
		return fileData;
	};

	// Upload file to S3
	const uploadFileToS3 = async (fileData, uploadFile) => {
		console.log('Uploading to S3...');
		const uploadResult = await uploadToS3(fileData.url, uploadFile, uploadFile.type);

		if (!uploadResult.success) {
			throw new Error('Failed to upload file to S3');
		}

		// Wait a moment for S3 to process the upload
		await new Promise((resolve) => {
			setTimeout(resolve, 2000);
		});
	};

	// Validate with backend for intercity trips
	const validateWithBackend = async (fileData, lrIdToUse, force) => {
		if (!isIntercity) return true;
		let additionalChargesProofKey = customerProofS3Key;
		if (isV2UI) {
			additionalChargesProofKey = customerProofS3Keys.length > 0 ? customerProofS3Keys : null;
		}

		// V1 API: Upload POD for validation
		const podRes = await dispatch(
			newPodUpload({
				data_key: fileData.filename,
				id: resolvedTripId,
				lrId: lrIdToUse,
				charges: createChargesObject(),
				...(force ? { pod_force_upload: true } : {}),
				additional_charges_proof_key: additionalChargesProofKey,
			})
		);

		return !handleBackendValidationError(podRes, force, null, lrIdToUse);
	};

	// Handle upload success
	const handleUploadSuccess = (fileData) => {
		if (!isV2UI) {
			setS3Key(fileData.filename);
		}
		setUploadError('');
		message.success(`${uploadTypeLabel} uploaded successfully!`);
	};

	// Handle upload error
	const handleUploadError = (error, force, uploadFile, lrIdToUse) => {
		console.error('Upload process error:', error);

		// Handle Axios errors (HTTP errors like 500)
		if (handleAxiosError(error, force, uploadFile, lrIdToUse)) {
			return false;
		}

		// Handle network or other errors
		if (!isV2UI) {
			setS3Key(null);
		}
		const fallbackErrorMessage = `${uploadTypeLabel} upload failed`;
		setUploadError(error.message || fallbackErrorMessage);
		return true;
	};

	// Main upload function
	const doUpload = async (uploadFile, force = false, lrIdToUse = null) => {
		if (!validateFileSize(uploadFile)) {
			return false;
		}

		setUploading(true);
		setUploadError('');

		try {
			const fileData = await getSignedUrlForUpload(uploadFile);
			await uploadFileToS3(fileData, uploadFile);

			if (!(await validateWithBackend(fileData, lrIdToUse, force))) {
				return false;
			}

			handleUploadSuccess(fileData);
		} catch (error) {
			handleUploadError(error, force, uploadFile, lrIdToUse);
		} finally {
			setUploading(false);
		}
		return false;
	};

	// Remove uploaded file
	const handleRemove = () => {
		if (!isV2UI) {
			setS3Key(null);
		}
		setUploadError('');
	};

	// Modal: Upload without LR
	const handlePodUploadYes = async () => {
		setShowPodQuestionModal(false);
		if (pendingUploadFile) {
			// Proceed with upload and CRN check, passing null for lrId
			await doUpload(pendingUploadFile, false, null);
			setPendingUploadFile(null);
		}
		return true;
	};

	const handlePodUploadNo = () => {
		setShowPodQuestionModal(false);
		setPendingUploadFile(null);
		resetAll();
	};

	// Modal: Force upload
	const handleForceUpload = async () => {
		try {
			if (pendingForceUploadFile) {
				await doUpload(
					pendingForceUploadFile.uploadFile,
					true,
					pendingForceUploadFile.lrIdToUse
				);
				setPendingForceUploadFile(null);
			}
		} finally {
			setShowForceUploadModal(false);
		}
		return true;
	};

	// Charges management
	const handleChange = (field, value) => {
		// Only allow digits 0-9
		if (!/^\d*$/.test(value)) {
			return;
		}

		setCharges((prev) => {
			let newValue = value;

			// If user clears the field, set to null
			if (value === '') {
				return { ...prev, [field]: null };
			}

			// Prevent leading zeros except for '0' itself
			if (/^0\d+/.test(newValue)) {
				newValue = newValue.replace(/^0+/, '');
			}

			// If the value is '0', keep it as '0', otherwise convert to number
			if (newValue === '0') {
				return { ...prev, [field]: '0' };
			}

			return { ...prev, [field]: newValue };
		});
	};

	const calcTotal = (fields) =>
		fields.reduce((sum, key) => {
			const value = charges[key];
			return sum + (value ? parseInt(value, 10) : 0);
		}, 0);

	const vendorTotal = calcTotal([
		'haltVendor',
		'miscVendor',
		'tollVendor',
		'unloadVendor',
		'extraKmVendor',
		'extraHrVendor',
		'labourVendor',
	]);
	const clientTotal = calcTotal([
		'haltClient',
		'miscClient',
		'tollClient',
		'unloadClient',
		'extraKmClient',
		'extraHrClient',
		'labourClient',
	]);

	// Reset all state
	const resetAll = () => {
		setDate(null);
		setS3Key(null);
		setPodS3KeysState([]);
		setOdometerS3KeyState('');
		setDriverPodPreviewKeys([]);
		setUploading(false);
		setUploadError('');
		setCharges(initialCharges());
		setShowPodQuestionModal(false);
		setPendingUploadFile(null);
		setShowForceUploadModal(false);
		setPendingForceUploadFile(null);
		setShowTripEndReasonModal(false);
		setTripEndReason(null);
		setCustomerProofS3Key(null);
		setCustomerProofS3KeysState([]);

		// Trigger page navigation refresh when modal closes
		refreshTableByPageNavigation();

		if (onClose) onClose();
	};

	// Trip end submission
	const hasRequiredOdometer = !shouldRequireOdometer || (isV2UI ? !!odometerS3Key : !!s3Key);
	const hasRequiredPod = !shouldRequirePod || (isV2UI ? podS3Keys.length > 0 : !!s3Key);
	const hasRequiredEndKm = shouldCollectEndKm ? !!endKm : true;
	const canSubmit =
		date &&
		hasRequiredEndKm &&
		hasRequiredOdometer &&
		hasRequiredPod &&
		!uploading &&
		!uploadingProof;

	// State for trip end reason when start and end KM are same
	const [tripEndReason, setTripEndReason] = useState(null);
	const [showTripEndReasonModal, setShowTripEndReasonModal] = useState(false);

	// Check if end KM is same as start KM and show modal
	// Only shows modal when user has finished typing and conditions are met
	const checkEndKmSameAsStart = useCallback(() => {
		const hasStartKm =
			trip?.start_km !== null && trip?.start_km !== undefined && trip?.start_km !== '';
		if (shouldCollectEndKm && !isIntercity && hasStartKm && endKm && !tripEndReason) {
			const startKm = trip?.start_km || 0;
			const endKmValue = Number(endKm) || 0;

			if (endKmValue === startKm && !showTripEndReasonModal && endKmValue > 0) {
				setShowTripEndReasonModal(true);
			}
		}
	}, [
		endKm,
		trip?.start_km,
		showTripEndReasonModal,
		isIntercity,
		shouldCollectEndKm,
		tripEndReason,
	]);

	useEffect(() => {
		const timer = setTimeout(() => {
			checkEndKmSameAsStart();
		}, 2000); // 2 second delay to allow user to finish typing

		return () => clearTimeout(timer);
	}, [endKm, checkEndKmSameAsStart]);

	useEffect(() => {
		const hasStartKm =
			trip?.start_km !== null && trip?.start_km !== undefined && trip?.start_km !== '';
		if (!hasStartKm) return;

		const startKm = trip?.start_km || 0;
		const endKmValue = Number(endKm) || 0;

		if (!endKm || endKmValue !== startKm) {
			setTripEndReason(null);
			setShowTripEndReasonModal(false);
		}
	}, [endKm, trip?.start_km]);

	// Customer Proof Upload Handler
	const handleCustomerProofUpload = async (uploadFile) => {
		if (isV2UI) {
			return false;
		}

		if (!validateFileSize(uploadFile)) {
			return false;
		}

		setUploadingProof(true);
		setUploadProofError('');

		try {
			const fileData = await getSignedUrlForUpload(uploadFile);
			await uploadFileToS3(fileData, uploadFile);
			setCustomerProofS3Key(fileData.filename);
			message.success('Customer proof uploaded successfully!');
		} catch (error) {
			console.error('Customer proof upload error:', error);
			setUploadProofError(error.message || 'Upload failed');
		} finally {
			setUploadingProof(false);
		}
		return false;
	};

	const handleCustomerProofRemove = () => {
		if (!isV2UI) {
			setCustomerProofS3Key(null);
		}
		setUploadProofError('');
	};

	// Validation helpers
	const validateEndKm = () => {
		if (!shouldCollectEndKm) {
			return true;
		}

		if (!endKm) {
			message.error('End KM is required');
			return false;
		}

		const endKmValue = Number(endKm);
		if (Number.isNaN(endKmValue)) {
			message.error('Please enter a valid End KM');
			return false;
		}

		const hasStartKm = trip?.start_km !== null;
		if (hasStartKm) {
			const startKm = trip?.start_km || 0;
			if (endKmValue < startKm) {
				message.error('End KM must be greater than start KM');
				return false;
			}
		}

		if (!isIntercity && hasStartKm) {
			const startKm = trip?.start_km || 0;
			const minRequiredEndKm = startKm + 250;
			if (endKmValue > minRequiredEndKm) {
				message.error(
					`End KM must be less than or equal to ${minRequiredEndKm} (Start KM: ${startKm} + 250 km maximum)`
				);
				return false;
			}
			if (endKmValue === startKm && !tripEndReason) {
				message.error('Please select a trip end reason when start and end KM are the same');
				return false;
			}
		}
		return true;
	};

	const validateTripDuration = () => {
		if (trip.trip_type === 'regular' && date) {
			const startTime = moment(trip.start_time);
			const endTime = moment(date);
			const durationInHours = endTime.diff(startTime, 'hours', true);
			if (durationInHours < 2) {
				message.error(`End time must be at least 2 hours after start time.`);
				return false;
			}
		}
		return true;
	};
	/* eslint-disable-next-line sonarjs/cognitive-complexity */
	const handleSubmit = async (e, extraData = {}) => {
		// Prevent form submission if called from button click
		if (e && e.preventDefault) {
			e.preventDefault();
		}
		if (e && e.stopPropagation) {
			e.stopPropagation();
		}

		if (!validateEndKm() || !validateTripDuration()) {
			return;
		}

		// Validate mandatory customer proof
		const isCustomerProofMissing = isV2UI
			? customerProofS3Keys.length === 0
			: !customerProofS3Key;

		if (clientTotal > 0 && isCustomerProofMissing) {
			message.error('Additional charges customer proof is mandatory when client charges > 0');
			return;
		}

		const proceedWithSubmit = async () => {
			setLoading(true);
			try {
				const normalizedTripId = resolvedTripId != null ? String(resolvedTripId) : null;
				if (!normalizedTripId) {
					throw new Error('Trip ID not found');
				}
				const sourceLocationValue =
					trip.trip_info?.source_location || trip.info?.source_location || '';
				const destinationLocationValue =
					trip.trip_info?.destination_location || trip.info?.destination_location || '';
				const infoPayload = {
					is_inter_city: trip.info?.is_inter_city ?? false,
					driver_name: trip.info?.driver_name || '',
					driver_number: trip.info?.driver_number || '',
					client_rate: trip.info?.client_rate ?? '',
					vendor_rate: trip.info?.vendor_rate ?? '',
					requirement_proof: trip.info?.requirement_proof || [],
				};
				let formattedStartDate = '';
				if (trip.start_date) {
					formattedStartDate = moment.utc(trip.start_date).format(DATE_FORMAT);
				}
				let formattedStartTime = '';
				if (trip.start_time) {
					formattedStartTime = moment.utc(trip.start_time).format(DATETIME_FORMAT);
				}

				const tripDocuments = isV2UI
					? {
							...trip.trip_documents,
							...(odometerS3Key
								? {
										odometer_end: odometerS3Key
											? [odometerS3Key]
											: trip.trip_documents?.odometer_end || [],
								  }
								: {
										odometer_end: trip.trip_documents?.odometer_end || [],
								  }),
					  }
					: (() => {
							if (isIntercity) {
								return { ...trip.trip_documents };
							}
							if (s3Key) {
								return {
									...trip.trip_documents,
									odometer_end: [s3Key],
								};
							}
							return {
								...trip.trip_documents,
								odometer_end: trip.trip_documents?.odometer_end || [],
							};
					  })();

				let targetPodId = trip.pod_id;

				const tripUpdateRes = await dispatch(
					addUser({
						data: {
							update_type: 'trip',
							data: {
								id: normalizedTripId,
								trip_id: normalizedTripId,
								...(targetPodId && { pod_id: targetPodId }),
								driver_id: trip.driver_id ? trip.driver_id : undefined,
								trip_type: trip.trip_type || 'adhoc',
								city: trip.city,
								project_id: trip.project_id ? trip.project_id : undefined,
								vendor_id: trip.vendor_id ? trip.vendor_id : undefined,
								vehicle_type: trip.vehicle_type,
								vehicle_number: trip.vehicle_number,
								...(!isV2UI
									? {
											reporting_date: trip.reporting_date
												? moment
														.utc(trip.reporting_date)
														.format('MM/DD/YYYY')
												: '',
											reporting_time: trip.reporting_time
												? moment
														.utc(trip.reporting_time)
														.format(DATETIME_FORMAT)
												: '',
											start_date: formattedStartDate,
											start_time: formattedStartTime,
									  }
									: {}),
								end_date: date
									? moment.utc(date).format(isV2UI ? V2_DATE_FORMAT : DATE_FORMAT)
									: '',
								end_time: date
									? moment
											.utc(date)
											.format(isV2UI ? V2_DATETIME_FORMAT : DATETIME_FORMAT)
									: '',
								...(shouldCollectEndKm ? { end_km: Number(endKm) } : {}),
								...(!isIntercity && Number(endKm) === (trip?.start_km || 0)
									? { trip_end_reason: tripEndReason }
									: {}),
								is_valid: true,
								...(sourceLocationValue && {
									source_location: sourceLocationValue,
								}),
								...(destinationLocationValue && {
									destination_location: destinationLocationValue,
								}),
								source_pincode: trip.source_pincode || '',
								destination_pincode: trip.destination_pincode || '',
								info: infoPayload,
								trip_documents: tripDocuments,
								// Per Order bllling type close
								...(Array.isArray(extraData.order_quantities)
									? { order_quantities: extraData.order_quantities }
									: {}),
								...(Array.isArray(extraData.proof_keys)
									? { proof_keys: extraData.proof_keys }
									: {}),
							},
						},
						isEditMode: true,
					})
				);

				if (!targetPodId && tripUpdateRes?.data?.pod_id) {
					targetPodId = tripUpdateRes.data.pod_id;
				} else if (!targetPodId && tripUpdateRes?.data?.proof_of_delivery?.id) {
					targetPodId = tripUpdateRes.data.proof_of_delivery.id;
				}

				if (isV2UI && !targetPodId) {
					try {
						const res = await dispatch(
							getListAction({
								search_type: 'trip',
								id: resolvedTripId,
								version: 'v2',
							})
						);
						if (res?.rows && res.rows.length > 0) {
							targetPodId = res.rows[0].pod_id || res.rows[0].proof_of_delivery?.id;
						}
					} catch (err) {
						console.error('Failed to fetch updated trip for POD id', err);
					}
				}

				// V1 API: Update POD
				const hasCharges = vendorTotal > 0 || clientTotal > 0;
				const hasProofs = isV2UI ? customerProofS3Keys.length > 0 : !!customerProofS3Key;
				let proofOfDeliveryChargeKey = null;
				if (hasProofs) {
					proofOfDeliveryChargeKey = isV2UI ? customerProofS3Keys : customerProofS3Key;
				}
				let syncPodAdditionalChargesKey = customerProofS3Key;
				if (isV2UI) {
					syncPodAdditionalChargesKey =
						customerProofS3Keys.length > 0 ? customerProofS3Keys : null;
				}

				if (
					(isV2UI
						? trip.pod_id || hasCharges || hasProofs || podS3Keys.length > 0
						: true) &&
					targetPodId
				) {
					await dispatch(
						updateProofOfDelivery({
							data: {
								update_type: 'proof_of_delivery',
								data: {
									id: targetPodId,
									...(isV2UI && { trip_id: resolvedTripId }),
									additional_charges_proof_key: proofOfDeliveryChargeKey,
									additional_charges: createChargesObject(),
								},
							},
						})
					);
				}

				// V1 API: Sync POD for intercity trips
				const shouldSyncPod = isV2UI
					? isPodRequired && podS3Keys && podS3Keys.length > 0
					: isIntercity && s3Key;

				if (shouldSyncPod) {
					const { lrId } = await fetchLrId();
					await dispatch(
						newPodUpload({
							data_key: isV2UI ? podS3Keys : s3Key,
							id: resolvedTripId,
							lrId,
							charges: createChargesObject(),
							pod_force_upload: true,
							additional_charges_proof_key: syncPodAdditionalChargesKey,
						})
					);
				}
				// }

				// A client can restrict closing to specific users. When the submitter is not
				// one of them the backend still records everything, but caps the trip at Trip
				// Ended and returns a notice saying who can close it, so do not claim it closed.
				const closureNotice = tripUpdateRes?.data?.notice;
				const persistedStatusId = Number(tripUpdateRes?.data?.trip_status_id);
				const didClose = persistedStatusId
					? persistedStatusId === TRIP_STATUS_ID.CLOSED
					: !closureNotice;

				if (didClose) {
					message.success('Trip closed successfully!');
				} else {
					message.info(
						closureNotice || 'Trip details saved. The trip remains in Trip Ended.'
					);
				}
				// Trigger table refresh using ref (async to avoid blocking)
				if (tableRefreshRef?.current) {
					// Call refresh asynchronously to avoid blocking modal close
					setTimeout(() => {
						try {
							tableRefreshRef.current();
						} catch (error) {
							console.error('Table refresh error:', error);
						}
					}, 100);
				}
				resetAll();
			} catch (error) {
				console.error('End trip error:', error);
				if (!handleAxiosError(error)) {
					message.error('Failed to end trip.');
				}
			} finally {
				setLoading(false);
			}
		};

		if (vendorTotal === 0 && clientTotal === 0 && !Array.isArray(extraData.order_quantities)) {
			Modal.confirm({
				title: 'Zero Charges Confirmation',
				content: 'All additional charges are 0, are you sure you want to proceed?',
				okText: 'Yes',
				cancelText: 'No',
				onOk: proceedWithSubmit,
			});
		} else {
			proceedWithSubmit();
		}
	};

	// Handle trip end reason submission
	const handleTripEndReasonSubmit = (reason) => {
		// Extract the value (id) from the selected reason object
		const reasonValue = reason?.value || reason;
		setTripEndReason(reasonValue);
		setShowTripEndReasonModal(false);
		// Don't auto-submit, let user click End Trip button
		message.success('Trip end reason selected successfully!');
	};

	// Handle trip end reason cancel
	const handleTripEndReasonCancel = () => {
		setShowTripEndReasonModal(false);
		setTripEndReason(null);
		// Clear the end KM if user cancels
		setEndKm('');
	};

	return {
		// State
		date,
		setDate,
		endKm,
		setEndKm,
		s3Key,
		podS3Keys,
		setPodS3Keys,
		odometerS3Key,
		setOdometerS3Key,
		uploading,
		uploadError,
		charges,
		loading,
		showPodQuestionModal,
		showForceUploadModal,
		showTripEndReasonModal,
		tripEndReason,
		trip_reason,
		vendorTotal,
		clientTotal,
		canSubmit,
		isIntercity,
		isPodRequired,
		isBackendEndTimeLocked,
		isBackendEndKmLocked,
		isComputedEndKmLocked,
		isPrefilledFromPreviousAttempt,

		// Actions
		handleUpload,
		handleRemove,
		handleChange,
		handleSubmit,
		resetAll,
		handlePodUploadYes,
		handlePodUploadNo,
		handleForceUpload,
		handleTripEndReasonSubmit,
		handleTripEndReasonCancel,
		setShowPodQuestionModal,
		setShowForceUploadModal,
		customerProofS3Key,
		refreshTableByPageNavigation,
		customerProofS3Keys,
		setCustomerProofS3Keys,
		uploadingProof,
		uploadProofError,
		handleCustomerProofUpload,
		handleCustomerProofRemove,
		driverPodPreviewKeys,
	};
};
